import { useState } from 'react';
import {
  eventPositions,
  mapLayers,
  mapPlaces,
  mapPoints,
  type MapLayer,
} from '../../../../fixtures/page01/map-data';
import { Card, EventStatus, Icon } from '../../../shared/ui/Primitives';
import type { ExampleEvent } from '../adapters/demo-reader';
import { searchEvents } from '../adapters/demo-reader';
import { useMapSummaryPosition } from './useMapSummaryPosition';

export function OverviewMap({
  events,
  selected,
  select,
  detail,
  explain,
}: {
  events: readonly ExampleEvent[];
  selected: ExampleEvent | undefined;
  select: (id: string) => void;
  detail: () => void;
  explain: (title: string, body: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [layers, setLayers] = useState<readonly MapLayer[]>(mapLayers.map((layer) => layer.id));
  const [query, setQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const term = query.trim();
  const matches = searchEvents(events, query);
  const points = mapPoints.filter((point) => point.name.includes(term));
  const places = mapPlaces.filter((place) => place.name.includes(term));
  const count = matches.length + points.length + places.length;
  const selectedVisible =
    selected && layers.includes('event') && matches.some((event) => event.id === selected.id);
  const { mapRef, summaryRef, placement } = useMapSummaryPosition(
    selectedVisible ? selected.id : undefined,
    `${zoom}:${expanded}:${layers.join(',')}:${query}`,
  );
  const summary = selectedVisible && (
    <div
      ref={summaryRef}
      className={`p01-map-summary${placement.docked ? ' p01-map-summary-docked' : ''}`}
      data-summary-event={selected.id}
      data-docked={placement.docked}
      style={placement.docked ? undefined : { left: placement.left, top: placement.top }}
    >
      <strong>{selected.name}</strong>
      <div>
        <EventStatus state={selected.state} />
        <time>{selected.time}</time>
      </div>
      <button className="p01-text-button" onClick={detail}>
        查看事件 <Icon name="arrow" />
      </button>
    </div>
  );
  return (
    <Card
      zone="Z03"
      title="辖区消防救援综合态势地图"
      className="p01-map-card"
      extra={
        <div className="p01-map-tools">
          <label className="p01-search">
            <Icon name="search" />
            <input
              aria-label="搜索示例地区、事件或点位"
              placeholder="搜索示例地区、事件或点位…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button aria-label="清除搜索" onClick={() => setQuery('')}>
                <Icon name="close" />
              </button>
            )}
          </label>
          <button
            aria-expanded={expanded}
            aria-controls="p01-layers"
            onClick={() => setExpanded(!expanded)}
          >
            <Icon name="layers" />
            图层
            <Icon name="chevron" />
          </button>
        </div>
      }
    >
      <div className="p01-map" ref={mapRef}>
        <div className="p01-map-world" data-zoom={zoom} style={{ transform: `scale(${zoom})` }}>
          <img src="/page01/Map_Basemap_Derived.png" alt="示意海湾底图" draggable={false} />
          {places.map((place) => (
            <span
              className="p01-map-place"
              key={place.name}
              style={{ left: `${place.x}%`, top: `${place.y}%` }}
            >
              {place.name}
            </span>
          ))}
          {layers.includes('event') &&
            matches.map((event) => {
              const position = eventPositions[event.id];
              return (
                <button
                  key={event.id}
                  data-map-event={event.id}
                  aria-label={`选择事件：${event.name}`}
                  aria-pressed={selected?.id === event.id}
                  className="p01-map-marker p01-marker-event"
                  style={{ left: `${position?.[0]}%`, top: `${position?.[1]}%` }}
                  onClick={() => select(event.id)}
                >
                  <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
                    <path d="M15 2 29 27H1Z" fill="currentColor" stroke="white" strokeWidth="2" />
                    <text x="15" y="23" textAnchor="middle" fill="white" fontSize="20">
                      !
                    </text>
                  </svg>
                </button>
              );
            })}
          {points
            .filter((point) => layers.includes(point.layer))
            .map((point) => (
              <button
                key={point.id}
                className={`p01-map-marker p01-marker-${point.layer}`}
                style={{ left: `${point.x}%`, top: `${point.y}%` }}
                aria-label={point.name}
                onClick={() =>
                  explain(
                    point.name,
                    '仅为示意地图的相对位置标记。未提供实际地址、地理坐标、资料来源或可调度状态。',
                  )
                }
              >
                {mapLayers.find((layer) => layer.id === point.layer)?.symbol}
              </button>
            ))}
        </div>
        {expanded && (
          <fieldset className="p01-layer-panel" id="p01-layers">
            <legend>图层控制</legend>
            {mapLayers.map((layer) => (
              <label key={layer.id}>
                <input
                  type="checkbox"
                  checked={layers.includes(layer.id)}
                  onChange={() =>
                    setLayers(
                      layers.includes(layer.id)
                        ? layers.filter((id) => id !== layer.id)
                        : [...layers, layer.id],
                    )
                  }
                />
                <span aria-hidden="true" className={`p01-symbol-${layer.id}`}>
                  {layer.symbol}
                </span>
                {layer.label}
              </label>
            ))}
          </fieldset>
        )}
        {term && (
          <div className="p01-search-result" role="status">
            {count
              ? `找到 ${count} 个示例地区、事件或点位；仅显示匹配项。`
              : '没有匹配的示例地区、事件或点位。'}
            <button onClick={() => setQuery('')}>清除搜索</button>
          </div>
        )}
        {!placement.docked && summary}
        <div className="p01-map-legend">
          <strong>图例</strong>
          {mapLayers
            .filter((layer) => layers.includes(layer.id))
            .map((layer) => (
              <span key={layer.id}>
                <i aria-hidden="true" className={`p01-symbol-${layer.id}`}>
                  {layer.symbol}
                </i>
                {layer.label}
              </span>
            ))}
          {layers.length === 0 && <span>所有图层已隐藏</span>}
        </div>
        <div className="p01-map-zoom">
          <button
            aria-label="放大地图"
            disabled={zoom >= 1.6}
            onClick={() => setZoom((value) => Math.min(1.6, value + 0.2))}
          >
            +
          </button>
          <button
            aria-label="缩小地图"
            disabled={zoom <= 1}
            onClick={() => setZoom((value) => Math.max(1, value - 0.2))}
          >
            −
          </button>
          <button aria-label="重置地图" onClick={() => setZoom(1)}>
            <Icon name="refresh" />
          </button>
        </div>
        <div className="p01-north" aria-label="示意北向">
          N<span>➤</span>
        </div>
        <span className="p01-map-caption">示意地图 · 非真实地理边界</span>
      </div>
      {placement.docked && summary}
      <p className="p01-map-note">
        搜索仅限示例地区、事件或点位。
        {selected && !selectedVisible && `当前选中：${selected.name}（标记已隐藏）`}
      </p>
    </Card>
  );
}
